import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/lib/format";
import { downloadBlob } from "@/components/AdminReports";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

function Totals({ d }) {
  const tiles = [["Total Received", d.total_received, "#10B981", "received"], ["Total Paid", d.total_paid, "#EF4444", "paid"], ["Closing Balance", d.closing_balance, "#1e3a5f", "balance"]];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {tiles.map(([l, v, c, k]) => (
        <Card key={k} className="p-4" data-testid={`party-total-${k}`}>
          <div className="text-xs text-muted-foreground">{l}</div>
          <div className="font-display text-2xl font-bold" style={{ color: c }}>{formatINR(v)}</div>
        </Card>
      ))}
    </div>
  );
}

function DocLinks({ docs }) {
  const view = async (x) => {
    const w = window.open("", "_blank");
    try { const r = await api.get(`/finance/${x.kind}/${x.id}/pdf`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); if (w) w.location.href = u; else window.open(u, "_blank"); }
    catch { w?.close(); toast.error("Could not open the document"); }
  };
  const dl = (x) => downloadBlob(`/finance/${x.kind}/${x.id}/pdf`, `${(x.number || x.kind).replace(/\//g, "-")}.pdf`);
  if (!docs.length) return "—";
  return docs.map((x) => (
    <div key={x.id} className="flex items-center gap-1 whitespace-nowrap">
      <span className={`text-[10px] font-semibold rounded px-1 ${x.kind === "MR" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>{x.kind}</span>
      <button className="text-primary hover:underline" onClick={() => view(x)} data-testid={`party-doc-view-${x.id}`}>{x.number}</button>
      <Button size="icon" variant="ghost" className="h-6 w-6" title="Download PDF" onClick={() => dl(x)} data-testid={`party-doc-download-${x.id}`}><Icons.Download className="h-3.5 w-3.5" /></Button>
    </div>
  ));
}

function LedgerTable({ d }) {
  const opening = d.date_from && <TableRow className="bg-secondary/30"><TableCell colSpan={9} className="text-sm font-medium">Opening balance (before {d.date_from})</TableCell><TableCell className="font-medium" data-testid="party-opening">{formatINR(d.opening_balance)}</TableCell></TableRow>;
  return (
    <Card className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow className="bg-secondary/50 hover:bg-secondary/50">
          {["Date", "Source", "Type", "Company", "Reference", "MR / PM", "Description", "Received", "Paid", "Balance"].map((h) => <TableHead key={h} className="overline text-muted-foreground whitespace-nowrap">{h}</TableHead>)}
        </TableRow></TableHeader>
        <TableBody>
          {d.order === "asc" && opening}
          {d.rows.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground">No entries for this party in this period.</TableCell></TableRow>}
          {d.rows.map((r) => (
            <TableRow key={r.id} data-testid={`party-row-${r.id}`}>
              <TableCell className="whitespace-nowrap">{r.date || "—"}</TableCell>
              <TableCell className="whitespace-nowrap text-xs">{r.source}</TableCell>
              <TableCell><Badge variant="secondary" className={r.type === "IN" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>{r.type}</Badge></TableCell>
              <TableCell>{r.issuer || "—"}</TableCell>
              <TableCell>{r.reference || "—"}</TableCell>
              <TableCell className="text-xs"><DocLinks docs={r.documents} /></TableCell>
              <TableCell className="max-w-[220px] truncate">{r.description || "—"}</TableCell>
              <TableCell className="text-emerald-700 whitespace-nowrap">{r.received ? formatINR(r.received) : ""}</TableCell>
              <TableCell className="text-red-700 whitespace-nowrap">{r.paid ? formatINR(r.paid) : ""}</TableCell>
              <TableCell className="font-medium whitespace-nowrap">{formatINR(r.balance)}</TableCell>
            </TableRow>
          ))}
          {d.order !== "asc" && opening}
        </TableBody>
      </Table>
    </Card>
  );
}

export default function PartyLedger() {
  const { isAdmin } = useAuth();
  const [parties, setParties] = useState(null);
  const [party, setParty] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState("desc");

  useEffect(() => { if (isAdmin) api.get("/party-ledger/parties").then((r) => setParties(r.data)).catch(() => {}); }, [isAdmin]);
  const params = useCallback(() => { const p = { party, order }; if (from) p.date_from = from; if (to) p.date_to = to; return p; }, [party, from, to, order]);
  const match = (parties || []).find((p) => p.toLowerCase() === party.trim().toLowerCase());
  useEffect(() => {
    if (!match) { setData(null); return; }
    setLoading(true);
    api.get("/party-ledger", { params: { ...params(), party: match } }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load")).finally(() => setLoading(false));
  }, [match, params]); // eslint-disable-line react-hooks/exhaustive-deps

  const exp = (format) => {
    const qs = new URLSearchParams({ ...params(), party: match, format }).toString();
    downloadBlob(`/party-ledger?${qs}`, `party_ledger_${party.replace(/[^a-z0-9]+/gi, "_")}.${format === "pdf" ? "pdf" : "xlsx"}`).then((ok) => ok && toast.success("Export downloaded"));
  };

  if (!isAdmin) return <div className="max-w-md mx-auto mt-16 text-center" data-testid="restricted-notice"><Icons.Lock className="h-8 w-8 mx-auto text-destructive mb-3" /><h2 className="font-display text-xl font-bold">Admins only</h2></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#1e3a5f1a", color: "#1e3a5f" }}><Icons.BookUser className="h-5 w-5" /></div>
        <div><h1 className="font-display text-2xl font-bold tracking-tight">Party Ledger</h1><p className="text-sm text-muted-foreground">Money Receipts, Paid Memos and ledger entries for one vendor / party</p></div>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[240px] max-w-md">
          <Input list="party-options" value={party} onChange={(e) => setParty(e.target.value)} placeholder="Search or select a vendor / party…" data-testid="party-select" />
          <datalist id="party-options">{(parties || []).map((p) => <option key={p} value={p} />)}</datalist>
        </div>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[150px]" data-testid="party-date-from" />
        <span className="text-xs text-muted-foreground pb-2">to</span>
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[150px]" data-testid="party-date-to" />
        <Button variant="outline" className="gap-1.5" onClick={() => setOrder((o) => (o === "desc" ? "asc" : "desc"))} data-testid="party-sort-toggle">
          {order === "desc" ? <Icons.ArrowDownWideNarrow className="h-4 w-4" /> : <Icons.ArrowUpNarrowWide className="h-4 w-4" />}{order === "desc" ? "Newest first" : "Oldest first"}
        </Button>
        {data && <><Button variant="outline" className="gap-1.5" onClick={() => exp("pdf")} data-testid="party-export-pdf"><Icons.FileText className="h-4 w-4" />PDF</Button>
          <Button variant="outline" className="gap-1.5" onClick={() => exp("excel")} data-testid="party-export-excel"><Icons.FileSpreadsheet className="h-4 w-4" />Excel</Button></>}
      </div>
      {!party && <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="party-empty">Pick a vendor or party to see their full ledger. {parties ? `${parties.length} parties on record.` : "Loading parties…"}</Card>}
      {party && parties && !match && <p className="text-sm text-muted-foreground">Keep typing or choose a name from the list.</p>}
      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {data && !loading && <>
        <Totals d={data} />
        <p className="text-xs text-muted-foreground" data-testid="party-counts">{data.counts.ledger} ledger entries · {data.counts.money_receipts} money receipts · {data.counts.paid_memos} paid memos. Each payment is counted once; receipts and memos are shown next to their ledger entry — click a number to view it or the download icon to save the PDF.</p>
        <LedgerTable d={data} />
      </>}
    </div>
  );
}
